import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Medication } from '@/types/medication';
import { v4 as uuidv4 } from 'uuid';
import { analyticsService } from '@/services/AnalyticsService';
import { databaseService } from '@/services/DatabaseService';
import { PROFILE_CONSTANTS, DEFAULT_PROFILE_ID, PROFILE_IDS } from '@/constants/profileConstants';
import { encryptData, decryptData, isCryptoSupported } from '@/utils/cryptoUtils';

/**
 * Cookie utility functions for persistent storage across sessions
 */
const CookieUtils = {
  /**
   * Set a persistent cookie that won't expire when the browser is closed
   * @param name - Cookie name
   * @param value - Cookie value
   * @param days - Days until expiration (default 365)
   */
  setCookie: (name: string, value: string, days = 365) => {
    try {
      // First clear any existing cookie with this name
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

      // Then set the new cookie with a far-future expiration
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = `expires=${date.toUTCString()}`;
      document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;


    } catch (error) {

    }
  },

  /**
   * Get a cookie value by name
   * @param name - Cookie name
   * @returns Cookie value or null if not found
   */
  getCookie: (name: string): string | null => {
    try {
      const nameEQ = `${name}=`;
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        const c = ca[i].trim();
        if (c.indexOf(nameEQ) === 0) {
          return c.substring(nameEQ.length, c.length);
        }
      }
      return null;
    } catch (error) {

      return null;
    }
  }
};

// Using imported constants from profileConstants.ts


interface MedicationState {
  // Map of profile ID -> day of week -> medications
  profileMedications: Record<string, Record<string, Medication[]>>;
  currentProfileId: string | null;
  isLoading: boolean;
  error: string | null;
  cache: {
    lastUpdated: number;
    upcomingDoses: Medication[];
    medicationsByDay: Record<string, {
      timestamp: number;
      data: Medication[];
    }>;
    recentlyAccessed: Set<string>;
  };
  setCurrentProfileId: (profileId: string) => void;
  setMedications: (medications: Record<string, Medication[]>, profileId?: string) => void;
  getMedicationsForCurrentProfile: () => Record<string, Medication[]>;
  addMedication: (day: string, medication: Medication) => void;
  updateMedication: (day: string, medication: Medication) => void;
  deleteMedication: (day: string, medicationId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  setUpcomingDoses: (doses: Medication[]) => void;
  markDoseTaken: (day: string, medicationId: string, taken: boolean) => void;
  clearCache: () => void;
  loadMedications: () => Promise<void>; // Hypothetical function for loading medications
}

// Selectors for optimized component updates
const selectMedicationsForDay = (state: MedicationState, day: string) => {
  const profileId = state.currentProfileId;
  if (!profileId || !state.profileMedications[profileId]) return [];
  return state.profileMedications[profileId][day] || [];
};
const selectUpcomingDoses = (state: MedicationState) => state.cache.upcomingDoses;
const selectIsLoading = (state: MedicationState) => state.isLoading;

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Create the medication store with profile-specific medication management
 */
export const useMedicationStore = create<MedicationState>()(
  persist(
    (set, get) => ({
      profileMedications: {},
      // CRITICAL FIX: ALWAYS default to 'u1' as the primary user
      // We're enforcing this by ignoring existing values and setting U1 explicitly
      currentProfileId: (() => {
        try {
          // DEBUG LOGGING: Check all current storage values before we touch anything
          const initialLocalStorage = localStorage.getItem(PROFILE_CONSTANTS.STORAGE_KEY);
          const initialCookie = CookieUtils.getCookie(PROFILE_CONSTANTS.COOKIE_KEY);
          const allCookies = document.cookie;



          // FORCE RESET to U1 regardless of existing values
          // This ensures consistent behavior across all devices and sessions
          const forcedDefaultId = DEFAULT_PROFILE_ID; // Always u1



          // Force update both storage mechanisms to ensure consistency
          localStorage.setItem(PROFILE_CONSTANTS.STORAGE_KEY, forcedDefaultId);


          // Aggressively clear ALL profile-related cookies
          document.cookie = "meduler_profile_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
          document.cookie = "currentProfileId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
          document.cookie = `${PROFILE_CONSTANTS.COOKIE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;


          // Set fresh cookie with U1 - using a very explicit approach
          const date = new Date();
          date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
          const expires = `expires=${date.toUTCString()}`;
          document.cookie = `${PROFILE_CONSTANTS.COOKIE_KEY}=${forcedDefaultId};${expires};path=/;SameSite=Lax`;


          // Verify our storage is consistent
          const finalLocalStorage = localStorage.getItem(PROFILE_CONSTANTS.STORAGE_KEY);
          const finalCookie = CookieUtils.getCookie(PROFILE_CONSTANTS.COOKIE_KEY);
          const finalAllCookies = document.cookie;



          return forcedDefaultId;
        } catch (error) {

          // Even if there's an error, still return U1
          return DEFAULT_PROFILE_ID;
        }
      })(),
      isLoading: false,
      error: null,
      cache: {
        lastUpdated: 0,
        upcomingDoses: [],
        medicationsByDay: daysOfWeek.reduce((acc, day) => ({
          ...acc,
          [day]: { timestamp: Date.now(), data: [] }
        }), {}),
        recentlyAccessed: new Set()
      },
      /**
       * Update the current profile ID and persist to both localStorage and cookies
       * @param profileId - The profile ID to set as current
       */
      setCurrentProfileId: (profileId) => {
        // Validate the profile ID - only allow 'u1' or 'u2'
        const validatedId = profileId === 'u1' || profileId === 'u2' ? profileId : DEFAULT_PROFILE_ID;

        // Persist to both localStorage and cookies for consistency
        localStorage.setItem(PROFILE_CONSTANTS.STORAGE_KEY, validatedId);
        CookieUtils.setCookie(PROFILE_CONSTANTS.COOKIE_KEY, validatedId);

        // Update state
        set({ currentProfileId: validatedId });

        // Load medications for the new profile
        const state = get();
        if (state.loadMedications) {
          state.loadMedications().catch(err => {

          });
        }
      },
      getMedicationsForCurrentProfile: () => {
        const state = get();
        const profileId = state.currentProfileId;
        if (!profileId || !state.profileMedications[profileId]) {
          return daysOfWeek.reduce((acc, day) => ({ ...acc, [day]: [] }), {});
        }
        return state.profileMedications[profileId];
      },
      setMedications: (medications, profileId) => {
        const now = Date.now();
        // Use the provided profile ID or the current profile ID
        const targetProfileId = profileId || get().currentProfileId;

        if (!targetProfileId) {

          return;
        }



        // Ensure all days have arrays, even if empty
        const normalizedMedications = daysOfWeek.reduce((acc, day) => ({
          ...acc,
          [day]: Array.isArray(medications[day]) ? medications[day] : []
        }), {});

        set((state) => {
          const updatedProfileMedications = {
            ...state.profileMedications,
            [targetProfileId]: normalizedMedications
          };

          return { 
            profileMedications: updatedProfileMedications,
            cache: {
              lastUpdated: now,
              upcomingDoses: [], 
              medicationsByDay: Object.entries(normalizedMedications).reduce((acc, [day, meds]) => ({
                ...acc,
                [day]: { timestamp: now, data: meds }
              }), {}),
              recentlyAccessed: new Set()
            }
          };
        });

        // Only log empty arrays in development
        if (process.env.NODE_ENV === 'development') {
          Object.entries(normalizedMedications).forEach(([day, meds]) => {
            if (!Array.isArray(meds)) {

            }
          });
        }
      },
      addMedication: (day, medication) =>
        set((state) => {
          try {
            // Critical log for medication addition troubleshooting
            console.log(`[DEBUGLOG] Adding medication to day: ${day}`, medication);
            console.log(`[DEBUGLOG] Medication timing value:`, medication.timing, typeof medication.timing);

            const profileId = state.currentProfileId;
            if (!profileId) {

              throw new Error('No current profile selected');
            }



            // Validate the medication object before saving
            if (!medication || typeof medication !== 'object') {

              throw new Error('Invalid medication object');
            }

            // Generate a helpful medication object with defaults for missing fields
            const validatedMedication = {
              ...medication,
              // Ensure ID is present
              id: medication.id || uuidv4(),
              // Provide a default name if missing
              name: medication.name || 'Unnamed Medication',
              // Ensure other critical fields have defaults
              dosage: medication.dosage || 'Standard dose',
              timing: medication.timing || 'morning',
              category: medication.category || 'daily'
            };

            // Log validation result
            if (!medication.name || !medication.id) {

            }


            const now = Date.now();

            // Get the current medications for this profile and day - initialize empty objects/arrays if missing
            const profileMeds = state.profileMedications[profileId] || 
              daysOfWeek.reduce((acc, d) => ({ ...acc, [d]: [] }), {});

            // If it's a cycle-based medication, add logging
            if (medication.category === 'cycle-based' || medication.cycle) {

            }

            // Ensure we're working with an array
            const existingMeds = Array.isArray(profileMeds[day]) ? profileMeds[day] : [];

            // Process medication to ensure all required fields are properly typed
            const processedMedication = {
              ...validatedMedication,
              // Ensure these are numbers not strings
              cycleLength: validatedMedication.cycleLength ? Number(validatedMedication.cycleLength) : undefined,
              activeLength: validatedMedication.activeLength ? Number(validatedMedication.activeLength) : undefined,
              inactiveLength: validatedMedication.inactiveLength ? Number(validatedMedication.inactiveLength) : undefined,
            };

            const updatedMeds = [...existingMeds, processedMedication];

            // Create updated profile medications with a deep copy to avoid reference issues
            const updatedProfileMeds = {
              ...JSON.parse(JSON.stringify(profileMeds)),
              [day]: updatedMeds,
            };

            // First update the local state to ensure immediate UI updates
            const newState = {
              profileMedications: {
                ...state.profileMedications,
                [profileId]: updatedProfileMeds
              },
              cache: {
                ...state.cache,
                medicationsByDay: {
                  ...state.cache.medicationsByDay,
                  [day]: { timestamp: now, data: updatedMeds }
                },
                recentlyAccessed: new Set([...state.cache.recentlyAccessed, day]).add(day)
              },
              error: null
            };

            // Then save to database with encryption - use profile ID as document ID
            try {
              // Flag to track if database operation is already complete
              let isOperationComplete = false;

              // Set a shorter timeout (5 seconds) to improve user experience
              const timeoutId = setTimeout(() => {
                if (!isOperationComplete) {

                  // Store the error state in session storage to prevent repeated errors
                  if (!sessionStorage.getItem('db_save_error_logged')) {
                    sessionStorage.setItem('db_save_error_logged', 'true');
                    // Reset error flag after 3 hours
                    setTimeout(() => sessionStorage.removeItem('db_save_error_logged'), 3 * 60 * 60 * 1000);
                  }
                }
              }, 5000);


              if (isCryptoSupported()) {
                // Encrypt the medications data
                const serializedData = JSON.stringify(updatedProfileMeds);


                encryptData(serializedData).then(encryptedData => {
                  isOperationComplete = true;
                  clearTimeout(timeoutId);


                  // Store with metadata indicating encryption
                  databaseService.saveData(
                    "medications", profileId, {
                      isEncrypted: true,
                      data: encryptedData,
                      lastUpdated: new Date().toISOString()
                    }
                  ).then(() => {

                  }).catch(error => {

                  });
                }).catch(error => {
                  isOperationComplete = true;
                  clearTimeout(timeoutId);

                  databaseService.saveData("medications", profileId, updatedProfileMeds)
                    .then(() => {

                    })
                    .catch(e => {

                      // Mark database as unavailable for this session to prevent repeated failures
                      sessionStorage.setItem('db_unavailable', 'true');
                    });
                });
              } else {
                // Fall back to unencrypted storage if crypto is not supported

                isOperationComplete = true;
                clearTimeout(timeoutId);
                databaseService.saveData("medications", profileId, updatedProfileMeds)
                  .then(() => {

                  })
                  .catch(e => {
                    console.error('Error:', e);
                  });
              }
            } catch (error) {

            }

            // Track the medication added event in analytics with medication details and profile ID
            analyticsService.trackMedicationAdded(
              {
                name: medication.name,
                dosage: medication.dosage,
                timing: medication.timing
              },
              profileId
            ).catch(error => {

            });

            return newState;
          } catch (error) {
            // More detailed error logging to help troubleshoot issues




            return {
              ...state,
              error: error?.message || 'Failed to add medication'
            };
          }
        }),
      updateMedication: (day, medication) =>
        set((state) => {
          const profileId = state.currentProfileId;
          if (!profileId) {

            return state;
          }



          // Get current profile medications
          const profileMeds = state.profileMedications[profileId] || {};
          if (!profileMeds[day]) {

            return state;
          }

          // Update medication in the list
          const updatedProfileMeds = {
            ...profileMeds,
            [day]: profileMeds[day].map((med) =>
              med.id === medication.id ? medication : med
            ),
          };

          // Save to database in background with encryption - use profile ID as document ID
          try {
            if (isCryptoSupported()) {
              // Encrypt the medications data
              const serializedData = JSON.stringify(updatedProfileMeds);
              encryptData(serializedData).then(encryptedData => {
                // Store with metadata indicating encryption
                databaseService.saveData(
                  "medications", profileId, {
                    isEncrypted: true,
                    data: encryptedData,
                    lastUpdated: new Date().toISOString()
                  }
                ).catch(error => {

                });
              }).catch(error => {

                // Fallback to unencrypted if encryption fails
                databaseService.saveData("medications", profileId, updatedProfileMeds)
                  .catch(e => {
                    console.error('Error:', e);
                  });
              });
            } else {
              // Fall back to unencrypted storage if crypto is not supported

              databaseService.saveData("medications", profileId, updatedProfileMeds)
                .catch(error => {
                  console.error('Error:', error);
                });
            }
          } catch (error) {

          }

          return {
            profileMedications: {
              ...state.profileMedications,
              [profileId]: updatedProfileMeds
            },
          };
        }),
      deleteMedication: (day, medicationId) =>
        set((state) => {
          const profileId = state.currentProfileId;
          if (!profileId) {

            return state;
          }



          // Get current profile medications
          const profileMeds = state.profileMedications[profileId] || {};
          if (!profileMeds[day]) {

            return state;
          }

          // Track medication archived with medication ID and profile ID
          analyticsService.trackMedicationArchived(medicationId, profileId).catch(error => {

          });

          // Create updated medication data with the medication removed
          const updatedProfileMeds = {
            ...profileMeds,
            [day]: profileMeds[day].filter((med) => med.id !== medicationId),
          };

          // Save to database with encryption
          try {
            if (isCryptoSupported()) {
              // Encrypt the medications data
              const serializedData = JSON.stringify(updatedProfileMeds);
              encryptData(serializedData).then(encryptedData => {
                // Store with metadata indicating encryption
                databaseService.saveData(
                  "medications", profileId, {
                    isEncrypted: true,
                    data: encryptedData,
                    lastUpdated: new Date().toISOString()
                  }
                ).catch(error => {

                });
              }).catch(error => {

                // Fallback to unencrypted if encryption fails
                databaseService.saveData("medications", profileId, updatedProfileMeds)
                  .catch(e => {
                    console.error('Error:', e);
                  });
              });
            } else {
              // Fall back to unencrypted storage if crypto is not supported

              databaseService.saveData("medications", profileId, updatedProfileMeds)
                .catch(error => {
                  console.error('Error:', error);
                });
            }
          } catch (error) {

          }

          return {
            profileMedications: {
              ...state.profileMedications,
              [profileId]: updatedProfileMeds
            },
          };
        }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      setUpcomingDoses: (doses) =>
        set((state) => ({
          cache: {
            ...state.cache,
            lastUpdated: Date.now(),
            upcomingDoses: doses,
          },
        })),
      markDoseTaken: (day, medicationId, taken) =>
        set((state) => {
          const profileId = state.currentProfileId;
          if (!profileId) {

            return state;
          }



          // Get current profile medications
          const profileMeds = state.profileMedications[profileId] || {};
          if (!profileMeds[day]) {

            return state;
          }

          // Track the medication taken or skipped in analytics with medication ID and profile ID
          if (taken) {
            analyticsService.trackMedicationTaken(medicationId, profileId).catch(error => {

            });

            // Also track generic feature usage
            analyticsService.trackFeatureUsage('adherenceLogged').catch(error => {

            });
          } else {
            analyticsService.trackMedicationSkipped(medicationId, profileId).catch(error => {

            });
          }

          // Update medication data with taken status
          const updatedProfileMeds = {
            ...profileMeds,
            [day]: profileMeds[day].map((med) =>
              med.id === medicationId ? { ...med, taken } : med
            ),
          };

          // Save to database with encryption
          try {
            if (isCryptoSupported()) {
              // Encrypt the medications data
              const serializedData = JSON.stringify(updatedProfileMeds);
              encryptData(serializedData).then(encryptedData => {
                // Store with metadata indicating encryption
                databaseService.saveData(
                  "medications", profileId, {
                    isEncrypted: true,
                    data: encryptedData,
                    lastUpdated: new Date().toISOString()
                  }
                ).catch(error => {

                });
              }).catch(error => {

                // Fallback to unencrypted if encryption fails
                databaseService.saveData("medications", profileId, updatedProfileMeds)
                  .catch(e => {
                    console.error('Error:', e);
                  });
              });
            } else {
              // Fall back to unencrypted storage if crypto is not supported

              databaseService.saveData("medications", profileId, updatedProfileMeds)
                .catch(error => {
                  console.error('Error:', error);
                });
            }
          } catch (error) {

          }

          return {
            profileMedications: {
              ...state.profileMedications,
              [profileId]: updatedProfileMeds
            },
          };
        }),
      clearCache: () =>
        set((state) => ({
          cache: {
            lastUpdated: 0,
            upcomingDoses: [],
            medicationsByDay: daysOfWeek.reduce((acc, day) => ({
              ...acc,
              [day]: { timestamp: 0, data: [] }
            }), {}),
            recentlyAccessed: new Set()
          },
        })),
      async loadMedications() {
        const now = Date.now();
        set({ isLoading: true, error: null });

        try {
          // Get current profile ID
          const profileId = get().currentProfileId || 'u1';


          // Try to load medications from database for the current profile
          try {
            const encryptedData = await databaseService.getData("medications", profileId);

            if (encryptedData && typeof encryptedData === 'object') {
              let medicationsData: Record<string, Medication[]>;

              // Type guard to check if data is encrypted
              interface EncryptedData {
                isEncrypted: boolean;
                data: string;
                lastUpdated?: string;
              }

              // Check if the data follows our encrypted format
              const isEncryptedData = (data: unknown): data is EncryptedData => {
                return !!data && 
                  typeof data === 'object' && 
                  'isEncrypted' in data && 
                  'data' in data &&
                  typeof (data as any).data === 'string';
              };

              // Check if the data is encrypted
              if (isEncryptedData(encryptedData)) {
                try {
                  // Decrypt the data
                  if (isCryptoSupported()) {
                    const decryptedData = await decryptData(encryptedData.data);
                    medicationsData = JSON.parse(decryptedData);
                  } else {

                    // We have to fall back to legacy format here
                    medicationsData = {} as Record<string, Medication[]>;
                  }
                } catch (decryptError) {

                  // Initialize with empty object if decryption fails
                  medicationsData = {} as Record<string, Medication[]>;
                }
              } else {
                // Handle legacy unencrypted data
                medicationsData = encryptedData as Record<string, Medication[]>;
              }

              // Update the store with loaded and decrypted data
              set((state) => ({
                profileMedications: {
                  ...state.profileMedications,
                  [profileId]: medicationsData as Record<string, Medication[]>
                },
                cache: {
                  lastUpdated: now,
                  upcomingDoses: [],
                  medicationsByDay: daysOfWeek.reduce((acc, day) => ({
                    ...acc,
                    [day]: { timestamp: now, data: (medicationsData as Record<string, Medication[]>)[day] || [] }
                  }), {}),
                  recentlyAccessed: new Set()
                },
                isLoading: false
              }));

              return;
            }
          } catch (dbError) {


            // Continue with initializing empty medications
          }

          // Initialize with empty arrays for all days if database load failed
          const initialMedications = daysOfWeek.reduce((acc, day) => ({
            ...acc,
            [day]: []
          }), {});

          // Initialize with empty medications for current profile
          set((state) => ({
            profileMedications: {
              ...state.profileMedications,
              [profileId]: initialMedications
            },
            cache: {
              lastUpdated: now,
              upcomingDoses: [],
              medicationsByDay: daysOfWeek.reduce((acc, day) => ({
                ...acc,
                [day]: { timestamp: now, data: [] }
              }), {}),
              recentlyAccessed: new Set()
            },
            isLoading: false
          }));

        } catch (error) {

          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to initialize medications'
          });
        }
      }
    }),
    {
      name: 'medication-store',
      partialize: (state) => ({
        profileMedications: state.profileMedications,
        currentProfileId: state.currentProfileId,
        cache: state.cache,
      }),
    }
  )
);


// Database service is now imported from @/services/DatabaseService